import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { Logo } from "@/components/logo";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PreCallLoading } from "@/features/pre-call/components/pre-call-loading";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

import { useCanUserJoin } from "./can-user-join";
import { useSubscribeToConcurrency } from "./subscribe-to-concurrency";

export function SessionPreview() {
  const { key } = useParams();

  const navigate = useNavigate();
  const setPin = useVidyoStore((state) => state.setPin);
  const setDisplayName = useVidyoStore((state) => state.setDisplayName);
  const setRoomKey = useVidyoStore((state) => state.setRoomKey);
  const { canJoinSession, session, user } = useCanUserJoin();
  const isVidyoReady = useVidyoStore((state) => state.isVidyoReady);
  const { nbOfParticipants, hasAvailableConcurrency, isConnected } =
    useSubscribeToConcurrency();

  const handleJoinSession = () => {
    if (!user.isSuccess || !session.isSuccess) {
      return;
    }

    setPin(Number(session.data.pin));

    setDisplayName(user.data.given_name + " " + user.data.family_name);
    setRoomKey(key);
    navigate(`/r/${key}/call`);
  };

  if (isVidyoReady) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <Logo className="mb-4 hidden md:inline-block" />
        <p className="mb-2 text-2xl text-foreground">
          {session.isSuccess ? session.data.name : "Ready to join?"}
        </p>
        {session.isSuccess && session.data.description && (
          <p className="mb-4 text-sm text-muted">{session.data.description}</p>
        )}

        {isConnected ? (
          <p className="my-8 text-sm text-muted">
            {nbOfParticipants === 0
              ? "No one else is here"
              : `${nbOfParticipants} ${nbOfParticipants === 1 ? "participant" : "participants"} in the call`}
          </p>
        ) : (
          <p className="my-8 text-sm text-muted">
            Looking for others on the call..
          </p>
        )}

        {isConnected && !hasAvailableConcurrency && (
          <Alert className="my-8 border-border p-2">
            <div className="border-l-2 border-warning p-2">
              <AlertTitle className="flex items-center text-sm text-warning">
                <AlertTriangle className="mr-2 h-4 w-4" />
                Concurrency Max Reached
              </AlertTitle>
              <AlertDescription className="text-sm text-secondary">
                You are unable to join this call as this organisation has
                reached it's maximum concurrency. Please contact a system
                administrator for more information.
              </AlertDescription>
            </div>
          </Alert>
        )}
        <Button
          size="lg"
          isLoading={!canJoinSession}
          onClick={handleJoinSession}
          disabled={!canJoinSession || !hasAvailableConcurrency}
        >
          Join now
        </Button>
      </motion.div>
    );
  }

  return <PreCallLoading />;
}
